import {
  AIIcon,
  AllForemanId,
  CacheKeys,
  ErrorRetry,
  PlanningActionType,
  PlanningWorkPackageSlider,
  SearchField,
  UnassignedForemanId,
  buildWorkPackageOwnerFilter,
  useAppUser,
  useDebounce,
  usePlanning,
  useSessionStorage,
} from '@bechtel/client-common';
import { GET_WORKPACKAGES } from '@bechtel/client-gql';
import {
  Box,
  ButtonAppearance,
  ButtonSize,
  Heading,
  Icon,
  IconSize,
  Pagination,
  SegmentedControl,
  SegmentedControlOption,
  SegmentedControlSize,
  Spinner,
  SpinnerSize,
  Text,
  ToastType,
  useModalState,
  useToastsDispatch,
} from '@bechtel/gds';
import {
  CommodityType,
  MilestoneFilterStatusFlag,
  MilestoneType,
  PagingInput,
  PersonaType,
  WorkPackage,
  WorkPackageFilterByTerm,
  WorkPackageFilterInput,
  WorkPackageList,
  WorkPackageSearchByEnum,
  WorkPackageSortByTerm,
} from '@bechtel/types';
import { get, includes, isEqual, map } from 'lodash';
import { FC, Fragment, Suspense, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { AiConfirmationModal } from './ai-confirmation-modal';
import { Drawer } from './drawer';
import { DrawerValuesProps, SortDirection, initialDrawerValues as defaultValues } from './drawer/drawer-state';
import { ListItem } from './item';
import {
  ListStyled,
  PaginationContainerStyled,
  PlanAIButtonContentStyled,
  PlanAIButtonStyled,
  PlanAIButtonTextStyled,
  SearchCanvasButtonStyled,
  SearchContainerStyled,
  WorkPackageHeaderStyled,
  WorkPackageHeaderTitleStyled,
  WorkPackageListContainerStyled,
} from './styled';
//@ts-ignore declaration not recognizing svg
import { NetworkStatus, useLazyQuery } from '@apollo/client';
import { ReactComponent as FilterIcon } from '@bechtel/client-assets/dist/images/filter.svg';
import { Trans, t } from '@lingui/macro';
import { usePlanningPermissions } from '~/hooks';
import { WorkPackagesListProps, planningWorkPackageLimit } from './types';

export const WorkPackagesList: FC<WorkPackagesListProps> = ({ setDrawerStatus }) => {
  const {
    canPlanAllMilestones,
    canPlanMilestones,
    canManageAIPlanning,
    canManageAIPlanningAll,
    canManageAIPlanningAssigned,
  } = usePlanningPermissions();
  const { user } = useAppUser();
  const {
    planningDispatch,
    selectedSuperintendent,
    selectedGeneralForeman,
    selectedForeman,
    isReplan,
    resetWorkPackagePager,
    aiPlannedMilestoneCount,
  } = usePlanning();
  const dispatchToast = useToastsDispatch();
  const [searchQuery, setSearchQuery] = useState('');
  const [planningWorkPackageSlider, setPlanningWorkPackageSlider] = useState<PlanningWorkPackageSlider>(
    PlanningWorkPackageSlider.Single
  );
  const [planningWpListCursor, setCursor] = useState<number>(1);
  const debouncedSearchQuery = useDebounce(searchQuery?.trim(), 500);
  const { close, toggle, visible } = useModalState(false);
  const { close: closeAiModal, open: openAiModal, visible: aiModalVisible } = useModalState(false);
  const [totalWorkPackages, setTotalWorkPackages] = useState(0);
  const [isAiRequestInProgress, setIsAiRequestInProgress] = useState(false);
  const shownToastForRequestId = useRef<string | null>(null);

  const wpDefaultDrawerValues = canPlanAllMilestones
    ? { ...defaultValues, searchScope: false }
    : user &&
        user.crewMember &&
        user.crewMember.persona &&
        (user.crewMember.persona === PersonaType.FOREMAN ||
          user.crewMember.persona === PersonaType.GENERAL_FOREMAN ||
          (user.crewMember.persona === PersonaType.SUPERINTENDENT && user.crewMember.id === selectedSuperintendent?.id))
      ? defaultValues
      : { ...defaultValues, searchScope: false };

  const persistedDrawerValues = useRef(wpDefaultDrawerValues);
  const [drawerValues, setDrawerValues] = useSessionStorage<DrawerValuesProps>(
    CacheKeys.PlanningWorkPackageDrawerValues,
    persistedDrawerValues.current || wpDefaultDrawerValues
  );

  const closeDrawer = () => {
    setTimeout(function () {
      close();
      setDrawerStatus(!visible);
    }, 0);
  };
  const [loadWorkpackages, { loading, data, error, refetch, called, networkStatus }] = useLazyQuery<
    { workPackages: WorkPackageList },
    {
      filter: WorkPackageFilterInput;
      paging: PagingInput;
    }
  >(GET_WORKPACKAGES, {
    notifyOnNetworkStatusChange: true,
    fetchPolicy: 'no-cache',
  });

  useEffect(() => {
    if (user?.crewMember?.persona === PersonaType.GENERAL_FOREMAN || user?.crewMember?.persona === PersonaType.FOREMAN)
      return;
    if (user?.crewMember?.persona === PersonaType.SUPERINTENDENT && user?.crewMember?.id === selectedSuperintendent?.id)
      setDrawerValues({ ...drawerValues, searchScope: true });
    else setDrawerValues({ ...drawerValues, searchScope: false });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedSuperintendent]);

  // Sync slider state to planning context
  useEffect(() => {
    planningDispatch({
      type: PlanningActionType.SetPlanningWorkPackageSlider,
      payload: { planningWorkPackageSlider },
    });
  }, [planningWorkPackageSlider, planningDispatch]);

  useEffect(() => {
    if (!resetWorkPackagePager) return; //default is false, it will be set to true at the time of changing schedule/ownership of work package
    //After edit ownership/schedule, slider needs to be set to Single and work package needs to be called from page1
    setPlanningWorkPackageSlider(PlanningWorkPackageSlider.Single);
    if (planningWpListCursor === 1) {
      //refetch called if work package being changed for ownership/schedule is at page 1.
      refetch();
    } else {
      //cursor set to 1 if work package being changed for ownership/schedule is not at page 1.
      setCursor(1);
    }
    //After completion of change ownership/schedule, this flag will be set to false so that work package list can be refreshed on next change for FM/GF/SUP.
    planningDispatch({
      type: PlanningActionType.ResetWorkPackageSelection,
      payload: { resetWorkPackagePager: false },
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resetWorkPackagePager, planningDispatch]);

  useEffect(() => {
    // Reset button when foreman changes OR when AI planning is saved/cancelled
    // Status changes: PROCESSING → COMPLETED → ACCEPTED (saved) or undefined (cancelled)
    const status = selectedForeman?.planningAiRequest?.status;
    const count = selectedForeman?.planningAiRequest?.count ?? 0;
    const requestId = selectedForeman?.planningAiRequest?.id;

    // Request is no longer in progress if:
    // - Status is final (ACCEPTED, REJECTED, ERRORED)
    // - No request exists (undefined)
    // - COMPLETED with no milestones (nothing to save/cancel)
    if (
      includes(['ACCEPTED', 'REJECTED', 'ERRORED'], status) ||
      status === undefined ||
      (status === 'COMPLETED' && count === 0)
    ) {
      setIsAiRequestInProgress(false);
      // Always reset toast tracking and remove toast when request is complete
      shownToastForRequestId.current = null;
      dispatchToast({ type: 'REMOVE_TOAST', payload: { id: 'ai-planning-success' } });
    }

    // Show spinner toast when switching to a foreman with PROCESSING AI request
    // Only show once per request ID to avoid duplicates
    if (status === 'PROCESSING' && requestId && shownToastForRequestId.current !== requestId) {
      shownToastForRequestId.current = requestId;
      // Remove existing toast before adding new one to prevent duplicates
      dispatchToast({ type: 'REMOVE_TOAST', payload: { id: 'ai-planning-success' } });
      dispatchToast({
        type: 'ADD_TOAST',
        payload: {
          id: 'ai-planning-success',
          toastType: ToastType.INFO,
          content: () => (
            <>
              <Spinner size={SpinnerSize.SMALL} />
              <span style={{ marginLeft: '12px' }}>
                <Trans>AI planning request submitted successfully. Please wait.</Trans>
              </span>
            </>
          ),
        },
      });
    } else if (status !== 'PROCESSING') {
      // Remove toast when status is not PROCESSING (switching to different foreman or status changed)
      // Always reset tracking when removing toast so it can be shown again when we return
      shownToastForRequestId.current = null;
      dispatchToast({ type: 'REMOVE_TOAST', payload: { id: 'ai-planning-success' } });
    }
  }, [
    selectedForeman?.id,
    selectedForeman?.planningAiRequest?.status,
    selectedForeman?.planningAiRequest?.count,
    selectedForeman?.planningAiRequest?.id,
    dispatchToast,
  ]);

  const filterOwners = useMemo(() => {
    return buildWorkPackageOwnerFilter({
      selectedSuperintendent,
      selectedGeneralForeman,
      selectedForeman,
      filterByUnassignedPersona: drawerValues?.filterByUnassignedPersona,
    });
  }, [selectedSuperintendent, selectedGeneralForeman, selectedForeman, drawerValues]);

  const defaultWorkPackages = useMemo(() => {
    return get(data, 'workPackages.items', []);
  }, [data]);

  const workPackageCount = get(data, 'workPackages.total');
  useEffect(() => {
    if (workPackageCount != null && workPackageCount != totalWorkPackages) {
      setTotalWorkPackages(workPackageCount);
    }
  }, [workPackageCount]);
  const totalPages = Math.ceil(totalWorkPackages / planningWorkPackageLimit);
  useEffect(() => {
    //isDefaultFilter added so that work package slider moves to single after reset filters
    if ((planningWorkPackageSlider !== PlanningWorkPackageSlider.Single && !isReplan) || isDefaultFilter) {
      setPlanningWorkPackageSlider(PlanningWorkPackageSlider.Single);
    }
    if (planningWpListCursor != 1) setCursor(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedSuperintendent, selectedGeneralForeman, selectedForeman, drawerValues, debouncedSearchQuery]);

  const workPackageFilterInput: WorkPackageFilterInput = {
    filterByOwners: filterOwners,
    startDate: drawerValues?.startDate,
    endDate: drawerValues?.endDate,
    searchKeyword: debouncedSearchQuery,
    searchBy: drawerValues?.searchBy ? drawerValues?.searchBy : [{ type: WorkPackageSearchByEnum.WP_NUMBER }],
    filterBy: drawerValues?.dateScope ? drawerValues?.dateScope : WorkPackageFilterByTerm.ALL,
    filterByMilestonesStatusFlags: drawerValues?.filterByMilestone,
    filterByCommodityTypeIds: map(drawerValues?.filterByCommodity, (item: CommodityType) => item.id),
    filterByMilestoneTypeIds: map(drawerValues?.filterByMilestoneTypes, (item: MilestoneType) => item.id),
    // assignedToMe: filterOwners && filterOwners.length == 0 ? false : drawerValues?.searchScope, //When no owners, pass scope as All WorkPackages
    assignedToMe: drawerValues?.searchScope,
    sortBy: drawerValues?.sortBy ? drawerValues?.sortBy : WorkPackageSortByTerm.WP_NUMBER,
    sortOrder: drawerValues?.sortDir ? drawerValues?.sortDir : SortDirection.DESC,
  };

  // Store filter in planning context for use in other components
  useEffect(() => {
    planningDispatch({
      type: PlanningActionType.SetWorkPackageFilterInput,
      payload: { workPackageFilterInput },
    });
  }, [workPackageFilterInput, planningDispatch]);

  useEffect(() => {
    if (debouncedSearchQuery.length > 0 && debouncedSearchQuery.length < 3) return;

    loadWorkpackages({
      variables: {
        filter: workPackageFilterInput,
        paging: {
          cursor: planningWpListCursor.toString(),
          limit: planningWorkPackageLimit,
        },
      },
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    workPackageFilterInput.filterByOwners,
    drawerValues,
    debouncedSearchQuery,
    loadWorkpackages,
    planningWpListCursor,
  ]);

  const onDrawerSubmit = useCallback(
    (values: DrawerValuesProps) => {
      setDrawerValues(() => {
        persistedDrawerValues.current = values;
        return values;
      });
    },
    [setDrawerValues]
  );

  useEffect(() => {
    if (isReplan) {
      onDrawerSubmit({ ...wpDefaultDrawerValues, filterByMilestone: [MilestoneFilterStatusFlag.EXPIRED] });
    }
    //needed to call the backlog data if user click on replan and move back to other module and come back here
    if (isReplan && drawerValues.filterByMilestone[0] === MilestoneFilterStatusFlag.EXPIRED) {
      setPlanningWorkPackageSlider(PlanningWorkPackageSlider.All);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isReplan]);

  useEffect(() => {
    if (isReplan && drawerValues.filterByMilestone[0] === MilestoneFilterStatusFlag.EXPIRED) {
      setPlanningWorkPackageSlider(PlanningWorkPackageSlider.All);
    }
    // checking resetWorkPackagePager false here to cater the need of selecting all work packages if 'All' was selected in slider and page number changed then next page should have all work packages selected. For other than 'All' value in slider, selected work package list should be empty
    if (resetWorkPackagePager == false && !isReplan && planningWorkPackageSlider !== PlanningWorkPackageSlider.All) {
      planningDispatch({
        type: PlanningActionType.ResetWorkPackageSelection,
        payload: { selectedWpList: [] },
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  useEffect(() => {
    if (
      planningWorkPackageSlider === PlanningWorkPackageSlider.Single ||
      planningWorkPackageSlider === PlanningWorkPackageSlider.Multiple
    )
      return;
    planningDispatch({
      type: PlanningActionType.AllWorkPackageSelected,
      payload: {
        selectedWpList: map(defaultWorkPackages, 'id'),
        totalSelectedCount: totalWorkPackages, // Pass the total count of all work packages
      },
    });
  }, [defaultWorkPackages, planningDispatch, planningWorkPackageSlider, totalWorkPackages]);

  //Start/End dates are getting changed w.r.t timezone while saving drawer values in session storage and hence comparison resulting in false always so converting dates here for comparison
  const isDefaultFilter = isEqual(
    { ...drawerValues, startDate: new Date(drawerValues.startDate), endDate: new Date(drawerValues.endDate) },
    {
      ...wpDefaultDrawerValues,
      startDate: new Date(wpDefaultDrawerValues.startDate),
      endDate: new Date(wpDefaultDrawerValues.endDate),
    }
  );

  // Check if logged-in user is selected in hierarchy for AI planning button visibility
  const isLoggedInUserSelectedInHierarchy = () => {
    const userCrewMemberId = user?.crewMember?.id;
    if (!userCrewMemberId) return false;

    // Check if logged-in user IS the selected foreman/GF/Superintendent
    return (
      userCrewMemberId === selectedForeman?.id ||
      userCrewMemberId === selectedGeneralForeman?.id ||
      userCrewMemberId === selectedSuperintendent?.id
    );
  };

  // Determine if AI Planning button should be visible
  const shouldShowAIPlanButton = () => {
    // Always show if user has ALL_WORK_PACKAGES scope
    if (canManageAIPlanningAll) return true;

    // For MY_WORK_PACKAGES scope, only show if logged-in user is selected in hierarchy
    if (canManageAIPlanningAssigned) {
      return isLoggedInUserSelectedInHierarchy();
    }

    return false;
  };

  return (
    <Fragment>
      <WorkPackageListContainerStyled data-testid="workpackage-list-container" style={{ position: 'relative' }}>
        <WorkPackageHeaderStyled>
          <WorkPackageHeaderTitleStyled>
            <Trans>Work Packages</Trans> {totalWorkPackages > 0 ? `(${totalWorkPackages})` : ``}
          </WorkPackageHeaderTitleStyled>
          {canPlanMilestones && shouldShowAIPlanButton() && (
            <PlanAIButtonStyled
              appearance={ButtonAppearance.PRIMARY}
              size={ButtonSize.SMALL}
              onClick={openAiModal}
              disabled={
                !selectedForeman ||
                selectedForeman.id === AllForemanId ||
                selectedForeman.id === UnassignedForemanId ||
                isAiRequestInProgress ||
                defaultWorkPackages.length === 0 ||
                // Disable when there's an active AI request (not yet accepted/rejected/errored)
                // OR when status is COMPLETED with milestones pending save/cancel
                (selectedForeman?.planningAiRequest?.id &&
                  (selectedForeman.planningAiRequest.status === 'PROCESSING' ||
                    (selectedForeman.planningAiRequest.status === 'COMPLETED' &&
                      (selectedForeman.planningAiRequest.count ?? 0) > 0)))
              }
            >
              <PlanAIButtonContentStyled>
                <AIIcon size={20} color="white" />
                <PlanAIButtonTextStyled>
                  <Trans>Plan with AI</Trans>
                </PlanAIButtonTextStyled>
              </PlanAIButtonContentStyled>
            </PlanAIButtonStyled>
          )}
        </WorkPackageHeaderStyled>
        <SearchContainerStyled>
          <Box mx={{ sm: `500` }} flexDirection={{ sm: 'column' }} flexGrow={{ sm: 1 }}>
            <Box my={{ sm: '500' }}>
              <SearchField
                iconSize={IconSize.MEDIUM}
                placeholder={t`Search`}
                type="search"
                onChange={(e) => setSearchQuery(e.target.value)}
                value={searchQuery}
              />
              <Box ml={{ sm: '400' }}>
                <SearchCanvasButtonStyled
                  appearance={ButtonAppearance.SECONDARY}
                  isDefaultFilterChange={!isDefaultFilter}
                  size={ButtonSize.MEDIUM}
                  onClick={() => {
                    toggle();
                    setDrawerStatus(!visible);
                  }}
                >
                  <Icon icon="sliders-v" size={IconSize.MEDIUM} fillColor={!isDefaultFilter ? 'white' : ''} />
                </SearchCanvasButtonStyled>
              </Box>
            </Box>

            <Text size="300" weight="600">
              <Trans>Select:</Trans>
            </Text>

            <Box my={{ sm: '400' }} data-testid="select-multiple-tab">
              <SegmentedControl segmentSize={SegmentedControlSize.SMALL} style={{ flexGrow: 1 }}>
                <SegmentedControlOption
                  segmentSize={SegmentedControlSize.SMALL}
                  selected={planningWorkPackageSlider === PlanningWorkPackageSlider.Single}
                  onClick={() => {
                    setPlanningWorkPackageSlider(PlanningWorkPackageSlider.Single);
                    planningDispatch({
                      type: PlanningActionType.ResetWorkPackageSelection,
                      payload: { selectedWpList: [] },
                    });
                  }}
                  data-testid={
                    planningWorkPackageSlider === PlanningWorkPackageSlider.Single
                      ? 'single-tab-selected'
                      : 'single-tab-unselected'
                  }
                >
                  <Trans>Single</Trans>
                </SegmentedControlOption>
                <SegmentedControlOption
                  segmentSize={SegmentedControlSize.SMALL}
                  selected={planningWorkPackageSlider === PlanningWorkPackageSlider.Multiple}
                  onClick={() => {
                    setPlanningWorkPackageSlider(PlanningWorkPackageSlider.Multiple);
                    planningDispatch({
                      type: PlanningActionType.ResetWorkPackageSelection,
                      payload: { selectedWpList: [] },
                    });
                  }}
                  data-testid={
                    planningWorkPackageSlider === PlanningWorkPackageSlider.Multiple
                      ? 'multi-tab-selected'
                      : 'multi-tab-unselected'
                  }
                >
                  <Trans>Multiple</Trans>
                </SegmentedControlOption>
                <SegmentedControlOption
                  disabled={false}
                  segmentSize={SegmentedControlSize.SMALL}
                  selected={planningWorkPackageSlider === PlanningWorkPackageSlider.All}
                  onClick={() => {
                    setPlanningWorkPackageSlider(PlanningWorkPackageSlider.All);
                  }}
                  data-testid={
                    planningWorkPackageSlider === PlanningWorkPackageSlider.All
                      ? 'all-tab-selected'
                      : 'all-tab-unselected'
                  }
                >
                  <Trans>All</Trans>
                </SegmentedControlOption>
              </SegmentedControl>
            </Box>
          </Box>
        </SearchContainerStyled>
        <ListStyled>
          {(!called || loading || networkStatus === NetworkStatus.refetch) && (
            <Box style={{ height: '100%' }} data-testid="loading">
              <Box
                flexGrow={{ sm: 1 }}
                alignItems={{ sm: 'center' }}
                flexDirection={{ sm: 'column' }}
                justifyContent={{ sm: 'center' }}
              >
                <Spinner size={SpinnerSize.MEDIUM} />
              </Box>
            </Box>
          )}
          {called && !loading && error && (
            <div data-testid="retry">
              <ErrorRetry refetch={refetch}>
                <Trans>
                  There was a problem loading work package list. Please click the button below to try again.
                </Trans>
              </ErrorRetry>
            </div>
          )}

          {called &&
            !loading &&
            !error &&
            (defaultWorkPackages.length > 0 ? (
              map(defaultWorkPackages, (wp: WorkPackage) => {
                return <ListItem wp={wp} planningWorkPackageSlider={planningWorkPackageSlider} key={wp.id} />;
              })
            ) : (
              <Box flexDirection={{ sm: 'column' }} alignItems={{ sm: 'center' }} pt={{ sm: '900' }}>
                <Heading size="400" weight="600" color="grey.600">
                  <Trans>No work packages found.</Trans>
                </Heading>
                <Box mt={{ sm: '700' }} mb={{ sm: '600' }} px={{ sm: '600' }}>
                  <Text
                    size="400"
                    family="graphik"
                    color="grey.600"
                    style={{ textAlign: 'center', lineHeight: '1.57' }}
                  >
                    <Trans>Try changing your search or filters to view a work package.</Trans>
                  </Text>
                </Box>
                <FilterIcon style={{ width: '124px', height: '110px' }} />
              </Box>
            ))}
        </ListStyled>
        {!loading && !error && defaultWorkPackages.length > 0 && (
          <PaginationContainerStyled>
            <Pagination
              selectable={false}
              totalPages={totalPages}
              onNext={() => {
                if (planningWpListCursor >= totalPages) return;
                setCursor(planningWpListCursor + 1);
                // resetWorkPackagePager set undefined here to cater the need of selecting all work packages if 'All' was selected in slider and page number changed then next page should have all work packages selected.
                planningDispatch({
                  type: PlanningActionType.ResetWorkPackageSelection,
                  payload: { resetWorkPackagePager: false },
                });
              }}
              onPrev={() => {
                if (planningWpListCursor <= 1) return;
                setCursor(planningWpListCursor - 1);
                // resetWorkPackagePager set undefined here to cater the need of selecting all work packages if 'All' was selected in slider and page number changed then next page should have all work packages selected.
                planningDispatch({
                  type: PlanningActionType.ResetWorkPackageSelection,
                  payload: { resetWorkPackagePager: false },
                });
              }}
              currentPage={planningWpListCursor.toString()}
              onPageChange={(page) => {
                setCursor(parseInt(page));
                // resetWorkPackagePager set undefined here to cater the need of selecting all work packages if 'All' was selected in slider and page number changed then next page should have all work packages selected.
                planningDispatch({
                  type: PlanningActionType.ResetWorkPackageSelection,
                  payload: { resetWorkPackagePager: false },
                });
              }}
            />
          </PaginationContainerStyled>
        )}
      </WorkPackageListContainerStyled>
      <Suspense fallback={<Spinner size={SpinnerSize.LARGE} />}>
        {visible && <Drawer close={closeDrawer} initialValues={drawerValues} onSubmit={onDrawerSubmit} />}
      </Suspense>
      <AiConfirmationModal
        visible={aiModalVisible}
        onClose={closeAiModal}
        onSuccess={() => setIsAiRequestInProgress(true)}
      />
    </Fragment>
  );
};
